import type { RenderableFile } from '@lib/types/files'

export function getDefaultThumbnailUrl(item: RenderableFile): string | null {
  const fileName = item.name || ''
  const fileExtension = fileName.split('.').pop()?.toLowerCase()

  const contentType = String(
    ('content_type' in item ? item.content_type : '') ||
      ('contentType' in item ? item.contentType : '') ||
      ('type' in item ? item.type : '') ||
      '',
  )

  const basePath = '/thumbnails'

  // Base thumbnail mappings
  const thumbnailMap = {
    pdf: 'pdf.png',
    ppt: 'ppt.png',
    pptx: 'ppt.png',
    jpg: 'jpg.png',
    jpeg: 'jpg.png',
    png: 'png.png',
    mp3: 'mp3.png',
    mp4: 'mp4.png',
    mov: 'mov.png',
    uzd: 'uzd.png',
    '3d': '3D.png',
    glb: '3D.png',
    gltf: '3D.png',
    zip: 'file.png',
    rar: 'file.png',
    '7z': 'file.png',
    tar: 'file.png',
    gz: 'file.png',
  }

  // Content type mappings
  const contentTypeMap: Record<string, string> = {
    // Short forms
    powerpoint: `${basePath}/${thumbnailMap.ppt}`,
    excel: `${basePath}/file.png`,
    word: `${basePath}/file.png`,
    image: `${basePath}/file-image.png`,
    video: `${basePath}/file-video.png`,
    audio: `${basePath}/file-audio.png`,
    archive: `${basePath}/file.png`,
    web: `${basePath}/file.png`,

    // MIME types
    'application/pdf': `${basePath}/${thumbnailMap.pdf}`,
    'application/vnd.ms-powerpoint': `${basePath}/${thumbnailMap.ppt}`,
    'application/vnd.openxmlformats-officedocument.presentationml.presentation': `${basePath}/${thumbnailMap.pptx}`,
    'image/jpeg': `${basePath}/${thumbnailMap.jpg}`,
    'image/jpg': `${basePath}/${thumbnailMap.jpeg}`,
    'image/png': `${basePath}/${thumbnailMap.png}`,
    'audio/mpeg': `${basePath}/${thumbnailMap.mp3}`,
    'audio/mp3': `${basePath}/${thumbnailMap.mp3}`,
    'video/mp4': `${basePath}/${thumbnailMap.mp4}`,
    'video/quicktime': `${basePath}/${thumbnailMap.mov}`,
    'model/gltf-binary': `${basePath}/${thumbnailMap['3d']}`,
    'model/gltf+json': `${basePath}/${thumbnailMap['3d']}`,
    'application/zip': `${basePath}/${thumbnailMap.zip}`,
    'application/x-zip-compressed': `${basePath}/${thumbnailMap.zip}`,
    'application/x-rar-compressed': `${basePath}/${thumbnailMap.rar}`,
    'application/x-7z-compressed': `${basePath}/${thumbnailMap['7z']}`,

    ...Object.fromEntries(Object.entries(thumbnailMap).map(([key, value]) => [key, `${basePath}/${value}`])),
  }

  const lowerFileName = fileName.toLowerCase()

  const filenamePatterns = [
    { patterns: ['thumbnail'], thumbnail: 'jpg.png' },
    { patterns: ['pptx', 'powerpoint', 'ppt', 'samplepptx'], thumbnail: 'ppt.png' },
    { patterns: ['file-sample', 'sample'], thumbnail: 'file.png' },
    { patterns: ['excel', 'xlsx', 'xls'], thumbnail: 'file.png' },
    { patterns: ['word', 'docx', 'doc', 'document', 'text'], thumbnail: 'file.png' },
    { patterns: ['mp4', 'video'], thumbnail: 'mp4.png' },
    { patterns: ['mp3', 'audio'], thumbnail: 'mp3.png' },
    { patterns: ['jpg', 'jpeg'], thumbnail: 'jpg.png' },
    { patterns: ['png'], thumbnail: 'png.png' },
    { patterns: ['image', 'photo', 'picture'], thumbnail: 'file-image.png' },
    { patterns: ['zip', 'rar', '7z', 'archive', 'compressed'], thumbnail: 'file.png' },
  ]

  for (const { patterns, thumbnail } of filenamePatterns) {
    if (patterns.some((pattern) => pattern === lowerFileName || lowerFileName.includes(pattern))) {
      return `${basePath}/${thumbnail}`
    }
  }

  if (contentType && contentTypeMap[contentType]) {
    return contentTypeMap[contentType]
  }

  if (fileExtension && thumbnailMap[fileExtension as keyof typeof thumbnailMap]) {
    return `${basePath}/${thumbnailMap[fileExtension as keyof typeof thumbnailMap]}`
  }

  if (contentType) {
    const categoryMap = {
      'audio/': 'file-audio.png',
      'image/': 'file-image.png',
      'video/': 'file-video.png',
    }

    for (const [prefix, thumbnail] of Object.entries(categoryMap)) {
      if (contentType.startsWith(prefix)) {
        return `${basePath}/${thumbnail}`
      }
    }
  }

  return `${basePath}/file.png`
}

export function onThumbnailError(event: Event): void {
  const img = event.target as HTMLImageElement
  img.style.display = 'none'
}
